import { createTheme, PaletteOptions, Theme } from '@mui/material/styles';
import { ComponentStyles, ThemeInput, DARK_TONES, DEFAULT_DARK_TONE, DarkToneName, refinedDarkComponents, refinedDarkPalette } from './refinedDark';

export type ThemeMode = 'light' | 'dark';

const lightPalette: PaletteOptions = {
  mode: 'light',
  primary: {
    main: '#1976d2',
    light: '#42a5f5',
    dark: '#1565c0',
  },
  secondary: {
    main: '#dc004e',
    light: '#ff5983',
    dark: '#9a0036',
  },
  background: {
    default: '#f5f5f5',
    paper: '#ffffff',
  },
  text: {
    primary: '#000000',
    secondary: '#666666',
  },
  divider: 'rgba(0, 0, 0, 0.12)',
  action: {
    hover: 'rgba(0, 0, 0, 0.04)',
    selected: 'rgba(0, 0, 0, 0.08)',
    disabled: 'rgba(0, 0, 0, 0.26)',
    disabledBackground: 'rgba(0, 0, 0, 0.12)',
  },
};

// Shared by both modes; dark mode layers refinedDark.ts on top.
const baseOptions = (): ThemeInput => ({
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    body1: {
      fontSize: '1rem',
    },
    body2: {
      fontSize: '0.875rem',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '8px 16px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: 'none',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
        },
      },
    },
  },
});

/** Overlay component styles slot by slot, so a refinement keeps the base's other slots. */
const mergeComponents = (base: ComponentStyles, extra: ComponentStyles): ComponentStyles => {
  const merged: Record<string, any> = { ...base };
  Object.entries(extra).forEach(([name, value]: [string, any]) => {
    merged[name] = {
      ...merged[name],
      ...value,
      styleOverrides: { ...merged[name]?.styleOverrides, ...value.styleOverrides },
    };
  });
  return merged as ComponentStyles;
};

/**
 * The app's MUI theme. Dark mode is the refined dark palette and surfaces in
 * the chosen tone (see refinedDark.ts). `surface` restyles a region that wears
 * its own colours, such as the sidebar (see rail.ts).
 */
export const buildTheme = (
  mode: ThemeMode,
  tone: DarkToneName = DEFAULT_DARK_TONE,
  surface: Pick<PaletteOptions, 'background' | 'text' | 'divider'> = {},
): Theme => {
  const options = baseOptions();
  if (mode === 'dark') {
    options.palette = { ...refinedDarkPalette(DARK_TONES[tone]), ...surface };
    options.components = mergeComponents(options.components ?? {}, refinedDarkComponents(DARK_TONES[tone]));
  } else {
    options.palette = { ...lightPalette, ...surface };
  }
  return createTheme(options);
};
